import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { templateParser } from 'utils/formatString';

import { Heading, Button, HTML, Preloader, Status } from 'components';
import withServiceConnection from 'pages/Home/containers/YouthTariff/hocs/withServiceConnection';

import classNames from 'classnames/bind';
import styles from './styles.pcss';
import ResultForm from './components/ResultForm';
const cx = classNames.bind(styles);

const steps = {
  loading: 'loading',
  conflict: 'conflict',
  connect: 'connect',
  connecting: 'connecting',
  complete: 'complete',
  errorResult: 'errorResult',
  timeout: 'timeout',
  error: 'error',
};

const mapServiceProcessToStep = {
  complete: steps.complete,
  error: steps.errorResult,
  timeout: steps.timeout,
};

const togglePendingStatuses = {
  loading: steps.loading,
  connecting: steps.connecting,
  connect: steps.connect,
  complete: steps.connect,
};

const ConnectionService = (props) => {
  const {
    content,
    serviceName,
    onSuccess,
    onSetPending,
    onTimeoutBtnClick,
    onErrorBtnClick,
    serviceConnectionPopup,
    connectService,
    serviceSoc,
  } = props;
  const [step, setStep] = useState(steps.loading);

  useEffect(() => {
    if (serviceSoc) {
      onSetPending({ soc: togglePendingStatuses[step] ? serviceSoc : null, connected: step === steps.complete });
    } else {
      onSetPending(!!togglePendingStatuses[step]);
    }

    if (step === steps.loading && serviceConnectionPopup?.data?.serviceConnectForm) {
      if (serviceConnectionPopup.data.serviceConnectForm.warningText) {
        setStep(steps.conflict);
      } else {
        setStep(steps.connect);
      }
      return;
    }

    const isInitialRequestError = serviceConnectionPopup
      && !serviceConnectionPopup.isPending
      && !serviceConnectionPopup.isSubmitting;
    if (step === steps.loading && isInitialRequestError) {
      setStep(steps.error);
      return;
    }

    if (step === steps.loading
      && !serviceConnectionPopup?.isSubmitting
      && !!serviceConnectionPopup?.resultPopupData
      && !serviceConnectionPopup?.data) {
      setStep(steps.errorResult);
      return;
    }

    if (step === steps.connect) {
      const startDate = new Date();
      const connectDate = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()));
      connectService({ connectDate });
      setStep(steps.connecting);
      return;
    }

    if (step === steps.connecting && serviceConnectionPopup.serviceProcess !== 'progress') {
      const newStep = mapServiceProcessToStep[serviceConnectionPopup.serviceProcess];

      setStep(newStep ?? steps.error);
    }
  }, [step, serviceConnectionPopup, connectService]);

  useEffect(() => {
    if (step === steps.complete) {
      onSuccess();
    }
  }, [step]);

  if (step === steps.complete) return null;
  if (step === steps.errorResult) {
    if (!serviceConnectionPopup) {
      return null;
    }

    const { resultPopupData } = serviceConnectionPopup;

    if (!resultPopupData) {
      return null;
    }

    return (
      <ResultForm
        title={resultPopupData.title}
        titleIcon={resultPopupData.titleIcon}
        content={resultPopupData.content}
        btnText={content.errorData.btn}
        onBtnClick={onErrorBtnClick}
      />
    );
  }

  if (step === steps.error) {
    return (
      <ResultForm
        title={content.errorData.title}
        emojiName="smile-sad"
        content={content.errorData.description}
        btnText={content.errorData.btn}
        onBtnClick={onErrorBtnClick}
      />
    );
  }

  return (
    <div className={cx('wrapper')}>
      {(step === steps.loading || step === steps.connecting) && (
        <div className={cx('connectingStep')}>
          <div className={cx('loaderWrap')}>
            <Preloader size="50" isStyled={false} />
          </div>
          <HTML as="div" className={cx('connectionWait')}>
            {content.connectionWait}
          </HTML>
        </div>
      )}
      {step === steps.timeout && (
        <>
          <HTML as="div" className={cx('title')}>{content.timeoutData.title}</HTML>
          <HTML as="div" className={cx('description', 'timeoutDescription')}>
            {templateParser(content.timeoutData.description, { serviceName })}
          </HTML>

          <div className={cx('btnWrap')}>
            <Button
              type="button"
              className="big"
              onClick={onTimeoutBtnClick}
              wide
            >
              {content.timeoutData.btn}
            </Button>
          </div>
        </>
      )}
      {step === steps.conflict && (
        <>
          <Heading level={1} isStyled={false}>
            {serviceName}
          </Heading>
          <Status emoji="pointingHandUp" className={['attention', cx('conflictsWarning')]}>
            <HTML>{serviceConnectionPopup.data.serviceConnectForm.warningText}</HTML>
          </Status>
          <div className={cx('btnWrap')}>
            <Button
              type="button"
              className="big"
              onClick={() => setStep('connect')}
              wide
            >
              {content.connectBtn}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

ConnectionService.propTypes = {
  content: PropTypes.shape({
    connectBtn: PropTypes.string,
    connectionWait: PropTypes.string,
    errorData: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      btn: PropTypes.string,
    }),
    timeoutData: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      btn: PropTypes.string,
    }),
  }),
  serviceName: PropTypes.string,
  serviceSoc: PropTypes.string,
  serviceConnectionPopup: PropTypes.shape({
    data: PropTypes.shape({
      serviceConnectForm: PropTypes.shape({
        warningText: PropTypes.string,
      }),
    }),
    resultPopupData: PropTypes.shape({
      title: PropTypes.string,
      titleIcon: PropTypes.string,
      content: PropTypes.string,
    }),
    serviceProcess: PropTypes.string,
    isPending: PropTypes.bool,
    isSubmitting: PropTypes.bool,
  }),
  onErrorBtnClick: PropTypes.func,
  onTimeoutBtnClick: PropTypes.func,
  onSuccess: PropTypes.func,
  onSetPending: PropTypes.func,
  connectService: PropTypes.func,
};

export default withServiceConnection(ConnectionService);
